import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Animated,
  StatusBar,
  Dimensions,
  Platform,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import authService, { LoginCredentials } from '../services/authService';

const { width, height } = Dimensions.get('window');

interface LoginScreenProps {
  onBackPress: () => void;
  onLoginSuccess: () => void;
  onForgotPasswordPress: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ 
  onBackPress, 
  onLoginSuccess,
  onForgotPasswordPress
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const slideInAnimation = useRef(new Animated.Value(width)).current;
  const fadeInAnimation = useRef(new Animated.Value(0)).current;
  const shakeAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.parallel([
              Animated.timing(slideInAnimation, {
          toValue: 0,
          duration: 800,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(fadeInAnimation, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
    ]);

    animation.start();
  }, []);

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setErrorMessage('Please enter both email and password');
      triggerShakeAnimation();
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const credentials: LoginCredentials = {
        email: email.trim().toLowerCase(),
        password: password,
      };

      const response = await authService.login(credentials);
      
      if (response.success) {
        console.log('Login successful:', response.data.user.email);
        onLoginSuccess();
      } else {
        setErrorMessage(response.message || 'Login failed');
        triggerShakeAnimation();
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setErrorMessage(error.message || 'Login failed. Please try again.');
      triggerShakeAnimation();
    } finally {
      setIsLoading(false);
    }
  };

  const triggerShakeAnimation = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, {
        toValue: 10,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(shakeAnimation, {
        toValue: -10,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(shakeAnimation, {
        toValue: 10,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(shakeAnimation, {
        toValue: 0,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password screen
    // This will be handled by the parent component
    onForgotPasswordPress();
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar 
        barStyle="dark-content" 
        backgroundColor="#FFFFFF"
        translucent={Platform.OS === 'android'}
      />
      
      <Animated.View
        style={[
          styles.content,
          {
            transform: [
              { translateX: slideInAnimation },
              { translateX: shakeAnimation },
            ],
            opacity: fadeInAnimation,
          },
        ]}
      >

        {/* Title */}
        <Text style={styles.title}>Log in</Text>

        {/* Error Message */}
        {errorMessage ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{errorMessage}</Text>
          </View>
        ) : null}

        {/* Form */}
        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Email address"
              placeholderTextColor="#9CA3AF"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                setErrorMessage('');
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isLoading}
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Password"
              placeholderTextColor="#9CA3AF"
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                setErrorMessage('');
              }}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isLoading}
            />
            <TouchableOpacity
              style={styles.eyeButton}
              onPress={togglePasswordVisibility}
              activeOpacity={0.7}
              disabled={isLoading}
            >
              <Text style={styles.eyeIcon}>👁</Text>
            </TouchableOpacity>
          </View>

          {/* Remember Me & Forgot Password */}
          <View style={styles.optionsContainer}>
            <TouchableOpacity
              style={styles.rememberMeContainer}
              onPress={toggleRememberMe}
              activeOpacity={0.7}
              disabled={isLoading}
            >
              <View style={styles.checkboxContainer}>
                <View style={[
                  styles.checkbox,
                  rememberMe && styles.checkboxChecked
                ]}>
                  {rememberMe && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.rememberMeText}>Remember me</Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity 
              activeOpacity={0.7}
              onPress={handleForgotPassword}
              disabled={isLoading}
            >
              <Text style={styles.forgotPasswordText}>Forgot password?</Text>
            </TouchableOpacity>
          </View>

          {/* Login Button */}
          <TouchableOpacity
            style={[
              styles.loginButton,
              isLoading && styles.loginButtonDisabled
            ]}
            onPress={handleLogin}
            activeOpacity={0.8}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.loginButtonText}>Log in</Text>
            )}
          </TouchableOpacity>
        </View>
      </Animated.View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    paddingHorizontal: Platform.select({ ios: 24, android: 24, default: 20 }),
    paddingTop: Platform.select({ ios: 130, android: 130, default: 130 }),
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 40, android: 40, default: 32 }),
  },
  title: {
    fontSize: Platform.select({ ios: 32, android: 32, default: 28 }),
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: Platform.select({ ios: 100, android: 100, default: 100 }),
    textAlign: 'center',
    fontFamily: 'Montserrat_700Bold',
    width: '100%',
    maxWidth: 450,
  },
  errorContainer: {
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: Platform.select({ ios: 8, android: 8, default: 6 }),
    padding: Platform.select({ ios: 12, android: 12, default: 10 }),
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
    width: '100%',
    maxWidth: 450,
  },
  errorText: {
    color: '#DC2626',
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    textAlign: 'center',
    ...Platform.select({
      web: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
    }),
  },
  form: {
    flex: 1,
    width: '100%',
    maxWidth: 450,
  },
  inputContainer: {
    position: 'relative',
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
  },
  input: {
    height: Platform.select({ ios: 56, android: 56, default: 48 }),
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: Platform.select({ ios: 12, android: 12, default: 10 }),
    paddingHorizontal: Platform.select({ ios: 16, android: 16, default: 14 }),
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
    color: '#111827',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Montserrat_400Regular',
  },
  eyeButton: {
    position: 'absolute',
    right: Platform.select({ ios: 16, android: 16, default: 14 }),
    top: Platform.select({ ios: 16, android: 16, default: 14 }),
    width: Platform.select({ ios: 24, android: 24, default: 20 }),
    height: Platform.select({ ios: 24, android: 24, default: 20 }),
    justifyContent: 'center',
    alignItems: 'center',
  },
  eyeIcon: {
    fontSize: Platform.select({ ios: 16, android: 16, default: 14 }),
  },
  optionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Platform.select({ ios: 20, android: 20, default: 16 }),
    paddingHorizontal: 4,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: Platform.select({ ios: 20, android: 20, default: 18 }),
    height: Platform.select({ ios: 20, android: 20, default: 18 }),
    borderWidth: 2,
    borderColor: '#D1D5DB',
    borderRadius: Platform.select({ ios: 4, android: 4, default: 3 }),
    marginRight: Platform.select({ ios: 8, android: 8, default: 6 }),
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#00506E',
    borderColor: '#00506E',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: Platform.select({ ios: 12, android: 12, default: 10 }),
    fontWeight: 'bold',
  },
  rememberMeText: {
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    color: '#374151',
    fontFamily: 'Montserrat_400Regular',
  },
  forgotPasswordText: {
    fontSize: Platform.select({ ios: 14, android: 14, default: 12 }),
    color: '#00506E',
    fontWeight: '500',
    fontFamily: 'Montserrat_600SemiBold',
  },
  loginButton: {
    backgroundColor: '#00506E',
    height: Platform.select({ ios: 56, android: 56, default: 48 }),
    borderRadius: Platform.select({ ios: 12, android: 12, default: 10 }),
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Platform.select({ ios: 160, android: 160, default: 160 }),
    marginBottom: Platform.select({ ios: 40, android: 40, default: 32 }),
  },
  loginButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  loginButtonText: {
    color: '#FFFFFF',
    fontSize: Platform.select({ ios: 18, android: 18, default: 16 }),
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
});
